import { SATQuestion } from '../../algebra-practice/data/algebraQuestions';

export interface MockExam3Question extends SATQuestion {
  domain: 'Algebra' | 'Advanced Math' | 'Problem-Solving & Data Analysis' | 'Geometry & Trigonometry';
}

export const mockExam3Questions: MockExam3Question[] = [
  // ── ALGEBRA (11 questions) ──────────────────────────────────────────────────
  {
    id: 1,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 7x − 4 = 3x + 16, what is the value of x?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '4' },
      { label: 'C', value: '5' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '7x − 4 = 3x + 16',
        'Subtract 3x from both sides: 4x − 4 = 16',
        'Add 4: 4x = 20',
        'Divide by 4: x = 5',
      ],
      explanation: 'Collect all x terms on one side, constants on the other, then divide.',
    },
  },
  {
    id: 2,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'Solve for y: (y/4) − 3 = 5',
    options: [
      { label: 'A', value: '8' },
      { label: 'B', value: '20' },
      { label: 'C', value: '24' },
      { label: 'D', value: '32' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: [
        'y/4 − 3 = 5',
        'Add 3 to both sides: y/4 = 8',
        'Multiply both sides by 4: y = 32',
      ],
      explanation: 'Isolate the fraction first by adding 3, then multiply both sides by 4.',
    },
  },
  {
    id: 3,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'A line has slope −2 and passes through (3, 1). What is the y-intercept?',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '7' },
      { label: 'C', value: '−5' },
      { label: 'D', value: '−7' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Use point-slope: y − 1 = −2(x − 3)',
        'y − 1 = −2x + 6',
        'y = −2x + 7',
        'y-intercept = 7',
      ],
      explanation: 'Substitute the point into point-slope form, then convert to slope-intercept form.',
    },
  },
  {
    id: 4,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'Solve the system: 3x + 2y = 16 and x − y = 2. What is x?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '4' },
      { label: 'C', value: '5' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'From x − y = 2: x = y + 2',
        'Substitute: 3(y + 2) + 2y = 16',
        '3y + 6 + 2y = 16 → 5y = 10 → y = 2',
        'x = y + 2 = 4',
      ],
      explanation: 'Use substitution: express x in terms of y, substitute, solve for y, then find x.',
    },
  },
  {
    id: 5,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'Solve: 6 − 4x &gt; 18',
    options: [
      { label: 'A', value: 'x &gt; −3' },
      { label: 'B', value: 'x &lt; −3' },
      { label: 'C', value: 'x &gt; 3' },
      { label: 'D', value: 'x &lt; 3' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '6 − 4x &gt; 18',
        'Subtract 6: −4x &gt; 12',
        'Divide by −4 (flip inequality): x &lt; −3',
      ],
      explanation: 'When dividing by a negative number, the inequality sign reverses direction.',
    },
  },
  {
    id: 6,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'Which equation represents a line parallel to y = 3x − 5?',
    options: [
      { label: 'A', value: 'y = −3x + 2' },
      { label: 'B', value: 'y = (1/3)x + 4' },
      { label: 'C', value: 'y = 3x + 8' },
      { label: 'D', value: 'y = −(1/3)x − 1' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Parallel lines have the same slope.',
        'The given line has slope 3.',
        'y = 3x + 8 also has slope 3 and a different y-intercept.',
      ],
      explanation: 'Parallel lines share the same slope but have different y-intercepts.',
    },
  },
  {
    id: 7,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 2(3x − 1) + 4 = 5x + 9, what is x?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '5' },
      { label: 'C', value: '7' },
      { label: 'D', value: '9' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Distribute: 6x − 2 + 4 = 5x + 9',
        '6x + 2 = 5x + 9',
        'Subtract 5x: x + 2 = 9',
        'x = 7',
      ],
      explanation: 'Expand, combine like terms on each side, then isolate x.',
    },
  },
  {
    id: 8,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'If 5x + 2y = 20 and 5x − 2y = 10, what is x?',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '3' },
      { label: 'C', value: '4' },
      { label: 'D', value: '5' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Add the two equations: 10x = 30',
        'Divide by 10: x = 3',
      ],
      explanation: 'Adding the equations eliminates y immediately, giving 10x = 30.',
    },
  },
  {
    id: 9,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'Which value of x satisfies both x &gt; −1 and x ≤ 4?',
    options: [
      { label: 'A', value: '−2' },
      { label: 'B', value: '0' },
      { label: 'C', value: '5' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Need x &gt; −1 AND x ≤ 4',
        'x = 0: 0 &gt; −1 ✓ and 0 ≤ 4 ✓',
        'x = −2 fails x &gt; −1; x = 5 and 6 fail x ≤ 4',
      ],
      explanation: 'The solution is the intersection: −1 &lt; x ≤ 4. Only x = 0 satisfies both.',
    },
  },
  {
    id: 10,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'A taxi charges a $3 base fee plus $2 per mile. Which equation gives the total cost C for m miles?',
    options: [
      { label: 'A', value: 'C = 3m + 2' },
      { label: 'B', value: 'C = 2m + 3' },
      { label: 'C', value: 'C = 5m' },
      { label: 'D', value: 'C = 2m − 3' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Base fee = $3 (constant, y-intercept)',
        'Rate = $2 per mile (slope)',
        'C = 2m + 3',
      ],
      explanation: 'In a linear model, the flat fee is the y-intercept and the per-unit rate is the slope.',
    },
  },
  {
    id: 11,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 3x + 4 = 2(x + 7), what is the value of 3x?',
    options: [
      { label: 'A', value: '10' },
      { label: 'B', value: '20' },
      { label: 'C', value: '30' },
      { label: 'D', value: '40' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '3x + 4 = 2x + 14',
        'Subtract 2x: x + 4 = 14',
        'x = 10',
        '3x = 30',
      ],
      explanation: 'Solve for x first, then multiply by 3 to get the answer directly.',
    },
  },

  // ── ADVANCED MATH (11 questions) ───────────────────────────────────────────
  {
    id: 12,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Equations',
    question: 'What are the solutions to 2x² − 8 = 0?',
    options: [
      { label: 'A', value: 'x = 2 only' },
      { label: 'B', value: 'x = ±2' },
      { label: 'C', value: 'x = ±4' },
      { label: 'D', value: 'x = 4 only' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '2x² = 8',
        'x² = 4',
        'x = ±√4 = ±2',
      ],
      explanation: 'Isolate x², then take the square root of both sides — remember the ± sign.',
    },
  },
  {
    id: 13,
    domain: 'Advanced Math',
    subtopic: 'Vertex Form',
    question: 'The parabola y = −(x − 1)² + 9 has a maximum. What is the maximum value?',
    options: [
      { label: 'A', value: '1' },
      { label: 'B', value: '−1' },
      { label: 'C', value: '9' },
      { label: 'D', value: '−9' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Vertex form: y = a(x − h)² + k',
        'a = −1 (opens downward), so vertex is a maximum',
        'k = 9 is the maximum value',
      ],
      explanation: 'When a &lt; 0, the parabola opens downward and the vertex is the maximum point.',
    },
  },
  {
    id: 14,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'What is the product of (x + 5)(x − 5)?',
    options: [
      { label: 'A', value: 'x² + 25' },
      { label: 'B', value: 'x² − 25' },
      { label: 'C', value: 'x² + 10x − 25' },
      { label: 'D', value: 'x² − 10x + 25' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Difference of squares: (a + b)(a − b) = a² − b²',
        '(x + 5)(x − 5) = x² − 25',
      ],
      explanation: 'Recognize the difference-of-squares pattern: (a + b)(a − b) = a² − b².',
    },
  },
  {
    id: 15,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'A bank account earns 5% interest per year. If the initial balance is $1,000, what is the balance after 2 years?',
    options: [
      { label: 'A', value: '$1,050' },
      { label: 'B', value: '$1,100' },
      { label: 'C', value: '$1,102.50' },
      { label: 'D', value: '$1,110' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'A = P(1 + r)^t = 1000(1.05)²',
        '= 1000 × 1.1025',
        '= $1,102.50',
      ],
      explanation: 'Compound interest formula: A = P(1 + r)^t. After year 1: $1,050; after year 2: $1,102.50.',
    },
  },
  {
    id: 16,
    domain: 'Advanced Math',
    subtopic: 'Rational Expressions',
    question: 'Simplify: (x² − 16) / (x − 4)',
    options: [
      { label: 'A', value: 'x − 4' },
      { label: 'B', value: 'x + 4' },
      { label: 'C', value: 'x² + 4' },
      { label: 'D', value: '(x + 4)(x − 4)' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Factor numerator: (x + 4)(x − 4)',
        'Cancel (x − 4): result = x + 4, x ≠ 4',
      ],
      explanation: 'Difference of squares: x² − 16 = (x + 4)(x − 4). Cancel the common factor.',
    },
  },
  {
    id: 17,
    domain: 'Advanced Math',
    subtopic: 'Radical Equations',
    question: 'Solve: √(2x + 1) = 7',
    options: [
      { label: 'A', value: '20' },
      { label: 'B', value: '24' },
      { label: 'C', value: '25' },
      { label: 'D', value: '48' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Square both sides: 2x + 1 = 49',
        'Subtract 1: 2x = 48',
        'Divide by 2: x = 24',
        'Check: √(48 + 1) = √49 = 7 ✓',
      ],
      explanation: 'Square both sides to remove the radical, then solve the resulting linear equation.',
    },
  },
  {
    id: 18,
    domain: 'Advanced Math',
    subtopic: 'Function Notation',
    question: 'If h(x) = 3x − x², what is h(4)?',
    options: [
      { label: 'A', value: '−4' },
      { label: 'B', value: '4' },
      { label: 'C', value: '−8' },
      { label: 'D', value: '8' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'h(4) = 3(4) − (4)²',
        '= 12 − 16',
        '= −4',
      ],
      explanation: 'Substitute x = 4 and evaluate: 3(4) = 12, (4)² = 16, so 12 − 16 = −4.',
    },
  },
  {
    id: 19,
    domain: 'Advanced Math',
    subtopic: 'Absolute Value Equations',
    question: 'Solve: |5x − 10| = 20',
    options: [
      { label: 'A', value: 'x = 6 or x = −2' },
      { label: 'B', value: 'x = 6 or x = 2' },
      { label: 'C', value: 'x = −6 or x = 2' },
      { label: 'D', value: 'x = 6 only' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Case 1: 5x − 10 = 20 → 5x = 30 → x = 6',
        'Case 2: 5x − 10 = −20 → 5x = −10 → x = −2',
      ],
      explanation: 'Split into two cases: the expression inside equals +20 or −20.',
    },
  },
  {
    id: 20,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Formula',
    question: 'Solve 3x² − 12x + 9 = 0 by factoring.',
    options: [
      { label: 'A', value: 'x = 1 and x = 3' },
      { label: 'B', value: 'x = −1 and x = −3' },
      { label: 'C', value: 'x = 3 and x = 9' },
      { label: 'D', value: 'x = 1 and x = 9' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Divide by 3: x² − 4x + 3 = 0',
        'Factor: (x − 1)(x − 3) = 0',
        'x = 1 or x = 3',
      ],
      explanation: 'Divide through by the GCF (3) first, then factor the simplified quadratic.',
    },
  },
  {
    id: 21,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'If p(x) = x³ − 3x² + 2x, what is p(3)?',
    options: [
      { label: 'A', value: '0' },
      { label: 'B', value: '3' },
      { label: 'C', value: '6' },
      { label: 'D', value: '9' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'p(3) = (3)³ − 3(3)² + 2(3)',
        '= 27 − 27 + 6',
        '= 6',
      ],
      explanation: 'Substitute x = 3 and evaluate each term carefully.',
    },
  },
  {
    id: 22,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'Which equation models a quantity that decreases by 20% each year starting from 500?',
    options: [
      { label: 'A', value: 'y = 500(1.2)^t' },
      { label: 'B', value: 'y = 500(0.8)^t' },
      { label: 'C', value: 'y = 500 − 20t' },
      { label: 'D', value: 'y = 500(0.2)^t' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Decay rate = 20%, so the multiplier = 1 − 0.20 = 0.80',
        'Exponential decay: y = 500(0.8)^t',
      ],
      explanation: 'For exponential decay, use y = P(1 − r)^t where r is the decay rate.',
    },
  },

  // ── PROBLEM-SOLVING & DATA ANALYSIS (11 questions) ─────────────────────────
  {
    id: 23,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'A machine produces 120 parts in 8 hours. How many parts does it produce in 15 hours?',
    options: [
      { label: 'A', value: '180' },
      { label: 'B', value: '200' },
      { label: 'C', value: '225' },
      { label: 'D', value: '240' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Rate = 120 / 8 = 15 parts per hour',
        'In 15 hours: 15 × 15 = 225 parts',
      ],
      explanation: 'Find the unit rate (parts per hour), then multiply by the new time.',
    },
  },
  {
    id: 24,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'A student scored 72 out of 90 on a test. What percent did the student score?',
    options: [
      { label: 'A', value: '72%' },
      { label: 'B', value: '78%' },
      { label: 'C', value: '80%' },
      { label: 'D', value: '82%' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Percent = (72 / 90) × 100',
        '= 0.8 × 100 = 80%',
      ],
      explanation: 'Divide the score by the total and multiply by 100.',
    },
  },
  {
    id: 25,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'Data set: 3, 7, 7, 8, 10, 12, 15. What is the median?',
    options: [
      { label: 'A', value: '7' },
      { label: 'B', value: '8' },
      { label: 'C', value: '9' },
      { label: 'D', value: '10' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Data is already ordered: 3, 7, 7, 8, 10, 12, 15',
        '7 values → median is the 4th value = 8',
      ],
      explanation: 'For an odd number of values, the median is the middle value after sorting.',
    },
  },
  {
    id: 26,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A bag contains 5 red, 3 blue, and 2 yellow marbles. What is the probability of drawing a red marble?',
    options: [
      { label: 'A', value: '1/2' },
      { label: 'B', value: '1/3' },
      { label: 'C', value: '2/5' },
      { label: 'D', value: '3/10' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Total marbles = 5 + 3 + 2 = 10',
        'P(red) = 5/10 = 1/2',
      ],
      explanation: 'Probability = favorable outcomes / total outcomes = 5/10 = 1/2.',
    },
  },
  {
    id: 27,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A survey of 200 students found that 60% prefer math over English. How many students prefer math?',
    options: [
      { label: 'A', value: '100' },
      { label: 'B', value: '110' },
      { label: 'C', value: '120' },
      { label: 'D', value: '130' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '60% of 200 = 0.60 × 200 = 120',
      ],
      explanation: 'Multiply the total by the percentage (as a decimal) to find the count.',
    },
  },
  {
    id: 28,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'The ratio of cats to dogs at a shelter is 3:5. If there are 40 dogs, how many cats are there?',
    options: [
      { label: 'A', value: '20' },
      { label: 'B', value: '24' },
      { label: 'C', value: '28' },
      { label: 'D', value: '32' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Ratio 3:5 means for every 5 dogs there are 3 cats',
        'Scale factor = 40 / 5 = 8',
        'Cats = 3 × 8 = 24',
      ],
      explanation: 'Find the scale factor by dividing the known quantity by its ratio part, then multiply.',
    },
  },
  {
    id: 29,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'A price increased from $50 to $65. What is the percent increase?',
    options: [
      { label: 'A', value: '20%' },
      { label: 'B', value: '25%' },
      { label: 'C', value: '30%' },
      { label: 'D', value: '35%' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Change = 65 − 50 = 15',
        'Percent increase = (15 / 50) × 100 = 30%',
      ],
      explanation: 'Percent change = (change ÷ original) × 100. Always divide by the original.',
    },
  },
  {
    id: 30,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'Five quiz scores are: 82, 90, 78, 95, 85. What is the mean score?',
    options: [
      { label: 'A', value: '84' },
      { label: 'B', value: '85' },
      { label: 'C', value: '86' },
      { label: 'D', value: '88' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Sum = 82 + 90 + 78 + 95 + 85 = 430',
        'Mean = 430 / 5 = 86',
      ],
      explanation: 'Mean = sum of all values ÷ number of values.',
    },
  },
  {
    id: 31,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A two-way table shows 80 students: 30 boys who like sports, 10 boys who don\'t, 25 girls who like sports, 15 girls who don\'t. What fraction of all students like sports?',
    options: [
      { label: 'A', value: '55/80' },
      { label: 'B', value: '30/80' },
      { label: 'C', value: '25/80' },
      { label: 'D', value: '40/80' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Students who like sports = 30 + 25 = 55',
        'Total = 80',
        'Fraction = 55/80',
      ],
      explanation: 'Add all students who like sports (boys + girls), then divide by the total.',
    },
  },
  {
    id: 32,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A card is drawn from a standard 52-card deck. What is the probability of drawing a king?',
    options: [
      { label: 'A', value: '1/52' },
      { label: 'B', value: '1/26' },
      { label: 'C', value: '1/13' },
      { label: 'D', value: '4/52' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'There are 4 kings in a 52-card deck',
        'P(king) = 4/52 = 1/13',
      ],
      explanation: '4/52 simplifies to 1/13. Both C and D are equivalent; 1/13 is the simplified form.',
    },
  },
  {
    id: 33,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'A data set has a range of 30 and a minimum of 12. What is the maximum?',
    options: [
      { label: 'A', value: '18' },
      { label: 'B', value: '30' },
      { label: 'C', value: '42' },
      { label: 'D', value: '48' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Range = Maximum − Minimum',
        '30 = Maximum − 12',
        'Maximum = 42',
      ],
      explanation: 'Rearrange the range formula: Maximum = Range + Minimum.',
    },
  },

  // ── GEOMETRY & TRIGONOMETRY (11 questions) ─────────────────────────────────
  {
    id: 34,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A square has perimeter 36 cm. What is its area?',
    options: [
      { label: 'A', value: '36 cm²' },
      { label: 'B', value: '64 cm²' },
      { label: 'C', value: '81 cm²' },
      { label: 'D', value: '144 cm²' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Perimeter = 4s → 36 = 4s → s = 9',
        'Area = s² = 9² = 81 cm²',
      ],
      explanation: 'Find the side length from the perimeter, then square it for the area.',
    },
  },
  {
    id: 35,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Pythagorean Theorem',
    question: 'A ladder 10 ft long leans against a wall. The base is 6 ft from the wall. How high up the wall does the ladder reach?',
    options: [
      { label: 'A', value: '6 ft' },
      { label: 'B', value: '7 ft' },
      { label: 'C', value: '8 ft' },
      { label: 'D', value: '9 ft' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'a² + b² = c²',
        '6² + h² = 10²',
        '36 + h² = 100',
        'h² = 64 → h = 8 ft',
      ],
      explanation: 'Recognize the 6-8-10 Pythagorean triple (scaled 3-4-5).',
    },
  },
  {
    id: 36,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Special Right Triangles',
    question: 'In a 30-60-90 triangle, the hypotenuse is 12. What is the length of the longer leg?',
    options: [
      { label: 'A', value: '6' },
      { label: 'B', value: '6√2' },
      { label: 'C', value: '6√3' },
      { label: 'D', value: '12√3' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '30-60-90 ratio: 1 : √3 : 2',
        'Hypotenuse = 12, so shorter leg = 6',
        'Longer leg = 6√3',
      ],
      explanation: 'In a 30-60-90 triangle, the longer leg = (hypotenuse/2) × √3.',
    },
  },
  {
    id: 37,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'In a right triangle, the opposite side = 7 and hypotenuse = 25. What is sin(θ)?',
    options: [
      { label: 'A', value: '7/24' },
      { label: 'B', value: '24/25' },
      { label: 'C', value: '7/25' },
      { label: 'D', value: '24/7' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'sin(θ) = opposite / hypotenuse',
        'sin(θ) = 7/25',
      ],
      explanation: 'SOH: sine = opposite over hypotenuse.',
    },
  },
  {
    id: 38,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the midpoint of (−4, 6) and (10, −2)?',
    options: [
      { label: 'A', value: '(3, 2)' },
      { label: 'B', value: '(6, 4)' },
      { label: 'C', value: '(3, 4)' },
      { label: 'D', value: '(7, 2)' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'M = ((−4 + 10)/2, (6 + (−2))/2)',
        '= (6/2, 4/2)',
        '= (3, 2)',
      ],
      explanation: 'Midpoint = average of x-coordinates, average of y-coordinates.',
    },
  },
  {
    id: 39,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Volume',
    question: 'A sphere has radius 3. What is its volume? (Use π ≈ 3.14)',
    options: [
      { label: 'A', value: '28.26' },
      { label: 'B', value: '56.52' },
      { label: 'C', value: '113.04' },
      { label: 'D', value: '226.08' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'V = (4/3)πr³',
        '= (4/3) × 3.14 × 27',
        '= (4/3) × 84.78',
        '= 113.04',
      ],
      explanation: 'Sphere volume = (4/3)πr³. Cube the radius first, then multiply.',
    },
  },
  {
    id: 40,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Lines & Angles',
    question: 'Two angles are complementary. One angle is 34°. What is the other?',
    options: [
      { label: 'A', value: '46°' },
      { label: 'B', value: '56°' },
      { label: 'C', value: '136°' },
      { label: 'D', value: '146°' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Complementary angles sum to 90°',
        'Other angle = 90° − 34° = 56°',
      ],
      explanation: 'Complementary angles add up to 90°.',
    },
  },
  {
    id: 41,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A circle has diameter 10. What is its circumference? (Use π ≈ 3.14)',
    options: [
      { label: 'A', value: '15.7' },
      { label: 'B', value: '31.4' },
      { label: 'C', value: '62.8' },
      { label: 'D', value: '78.5' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'C = πd',
        'C = 3.14 × 10 = 31.4',
      ],
      explanation: 'Circumference = π × diameter. The radius is half the diameter.',
    },
  },
  {
    id: 42,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the distance between (0, 0) and (5, 12)?',
    options: [
      { label: 'A', value: '10' },
      { label: 'B', value: '12' },
      { label: 'C', value: '13' },
      { label: 'D', value: '17' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'd = √(5² + 12²)',
        '= √(25 + 144)',
        '= √169 = 13',
      ],
      explanation: 'Recognize the 5-12-13 Pythagorean triple.',
    },
  },
  {
    id: 43,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'What is cos(60°)?',
    options: [
      { label: 'A', value: '√3/2' },
      { label: 'B', value: '1/2' },
      { label: 'C', value: '√2/2' },
      { label: 'D', value: '1' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'In a 30-60-90 triangle: cos(60°) = adjacent/hypotenuse = 1/2',
      ],
      explanation: 'cos(60°) = 1/2 is a standard value to memorize.',
    },
  },
  {
    id: 44,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Volume',
    question: 'A pyramid has a square base with side 6 and height 4. What is its volume?',
    options: [
      { label: 'A', value: '24' },
      { label: 'B', value: '48' },
      { label: 'C', value: '72' },
      { label: 'D', value: '144' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'V = (1/3) × base area × height',
        'Base area = 6² = 36',
        'V = (1/3) × 36 × 4 = 48',
      ],
      explanation: 'Pyramid volume = (1/3) × base area × height.',
    },
  },
];

export const EXAM3_DOMAIN_COLORS: Record<MockExam3Question['domain'], string> = {
  'Algebra': 'indigo',
  'Advanced Math': 'fuchsia',
  'Problem-Solving & Data Analysis': 'lime',
  'Geometry & Trigonometry': 'red',
};
